"use client";

import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { safeInternalPath } from "@repo/utils";
import { getAuthErrorMessage } from "@saas/auth/client";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { useRouter } from "@shared/hooks/router";
import { useSearch } from "@tanstack/react-router";
import { Button } from "@ui/components/button";
import { LinkButton } from "@ui/components/link-button";
import { Spinner } from "@ui/components/spinner";
import { ArrowLeftIcon } from "lucide-react";
import { useState } from "react";
import { AuthError, authErrorInput } from "./AuthNotice";
import { CODE_LENGTH, CodeInput } from "./CodeInput";

/** Second step of a password sign-in when two-factor is on. */
export function OtpForm() {
	const router = useRouter();
	const searchParams = useSearch({ strict: false }) as {
		invitationId?: string;
		redirectTo?: string;
	};
	// safeInternalPath: redirectTo comes straight off the query string, so an
	// unchecked value here is an open redirect once it reaches router.replace.
	const redirectTo = safeInternalPath(searchParams.redirectTo);
	const redirectPath = searchParams.invitationId
		? `/organization-invitation/${searchParams.invitationId}`
		: (redirectTo ?? config.auth.redirectAfterSignIn);

	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [verifying, setVerifying] = useState(false);

	const verify = async (value: string) => {
		if (value.length !== CODE_LENGTH || verifying) {
			return;
		}
		setError(null);
		setVerifying(true);
		try {
			const { error: verifyError } =
				await authClient.twoFactor.verifyTotp({
					code: value,
				});
			if (verifyError) {
				throw verifyError;
			}
			router.replace(redirectPath);
		} catch (caught) {
			setError(getAuthErrorMessage(authErrorInput(caught)));
			setCode("");
			setVerifying(false);
		}
	};

	return (
		<div className="flex flex-col">
			<AuthHeading
				title="Enter your 6-digit code"
				description="From your authenticator app."
			/>
			<form
				className="flex flex-col gap-5"
				onSubmit={(event) => {
					event.preventDefault();
					verify(code);
				}}
			>
				<AuthError message={error} />
				<CodeInput
					value={code}
					onChange={setCode}
					onComplete={verify}
					disabled={verifying}
				/>
				<Button
					type="submit"
					size="lg"
					disabled={code.length < CODE_LENGTH || verifying}
				>
					{verifying ? <Spinner data-icon="inline-start" /> : null}
					Verify and sign in
				</Button>
				<LinkButton to="/login" variant="ghost" className="self-center">
					<ArrowLeftIcon data-icon="inline-start" />
					Back to sign in
				</LinkButton>
			</form>
		</div>
	);
}
